import React, { useState } from 'react';
import { Application } from '../types';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotificationsIcon from '@mui/icons-material/Notifications'; // Изменили импорт на залитую версию колокольчика
import { Link } from 'react-router-dom';

interface ApplicationsTableProps {
  applications: Application[];
  loading: boolean;
  onViewStudents: (id: string) => void;
  onSendEmail: (id: string) => void;
  onDelete: (id: string) => void;
  onGenerateDocument?: (id: string) => void;
}

const VISIBLE_STUDENTS_COUNT = 3;

const ApplicationsTable: React.FC<ApplicationsTableProps> = ({
  applications,
  loading,
  onViewStudents,
  onSendEmail,
  onDelete,
  onGenerateDocument,
}) => {
  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set());

  const toggleStudentsExpand = (applicationId: string) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  const getStatusColor = (status: string) => {
    const s = status?.toLowerCase() || '';

    if (s === 'новая' || s === 'new') return 'bg-blue-100 text-blue-800';
    if (s === 'отправлено' || s === 'sent') return 'bg-green-100 text-green-800';
    if (s.includes('завершена') || s === 'completed') return 'bg-green-100 text-green-800';
    if (s.includes('отклонена') || s === 'rejected') return 'bg-red-100 text-red-800';
    if (s.includes('в обработке') || s === 'in_progress') return 'bg-yellow-100 text-yellow-800';

    return 'bg-gray-100 text-gray-800';
  };

  const formatDateInMoscow = (isoString: string) => {
    if (!isoString) return '—';

    try {
      const utcString = isoString.endsWith('Z') ? isoString : `${isoString}Z`;

      return new Date(utcString).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return isoString;
    }
  };

  const renderStudentsList = (application: Application) => {
    const students = application.students || [];

    if (students.length === 0) {
      return <span className="text-gray-400 italic">—</span>;
    }

    const isExpanded = expandedStudents.has(application.id);
    const hasMore = students.length > VISIBLE_STUDENTS_COUNT;
    const visibleStudents = isExpanded ? students : students.slice(0, VISIBLE_STUDENTS_COUNT);
    const hiddenCount = students.length - VISIBLE_STUDENTS_COUNT;

    return (
      <div className="flex flex-col">

        {/* Список ФИО */}
        <div className="flex flex-col gap-1.5 text-sm text-gray-500">
          {visibleStudents.map((student, index) => (
            <div key={student.id || index} className="leading-snug">
              {student.fullName}
            </div>
          ))}
        </div>

        {/* Кнопка раскрытия */}
        {hasMore && (
          <div className="mt-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleStudentsExpand(application.id);
              }}
              className="inline-flex items-center gap-0.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ExpandLessIcon sx={{ fontSize: 14 }} />
                  <span>Скрыть</span>
                </>
              ) : (
                <>
                  <ExpandMoreIcon sx={{ fontSize: 14 }} />
                  <span>Ещё {hiddenCount}</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Едва заметная линия + счётчик */}
        <div className="mt-2 pt-1.5 border-t border-gray-200">
          <span className="text-xs text-blue-600">
            Количество слушателей: {students.length}
          </span>
        </div>

      </div>
    );
  };

  return (
    <div className="w-full">
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <p className="text-gray-500">Загрузка данных...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Организация</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Специалист</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Студенты</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Папка</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дата (МСК)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.length > 0 ? (
                applications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {application.id}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(application.status)}`}
                      >
                        {application.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-[200px] whitespace-normal break-words">
                      {application.organization}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-[180px] whitespace-normal break-words">
                      {application.specialist}
                    </td>
                    <td className="px-4 py-4 max-w-[220px]">
                      {renderStudentsList(application)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-[120px] whitespace-normal break-words">
                      {application.examFolder}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateInMoscow(application.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1">
                        <Link
                          to={`/application/edit/${application.id}`}
                          className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-gray-100 transition"
                          title="Редактировать"
                        >
                          <EditIcon sx={{ fontSize: 20 }} />
                        </Link>
                        <button
                          onClick={() => onViewStudents(application.id)}
                          className="p-2 text-gray-400 hover:text-green-600 rounded-full hover:bg-gray-100 transition"
                          title="Студенты"
                        >
                          <VisibilityIcon sx={{ fontSize: 20 }} />
                        </button>

                        {/* Кнопка перехода к списку уведомлений с залитой иконкой колокольчика */}
                        <Link
                          to={`/applications/${application.id}/notifications`}
                          className="p-2 text-gray-400 hover:text-orange-500 rounded-full hover:bg-gray-100 transition"
                          title="Лог уведомлений"
                        >
                          <NotificationsIcon sx={{ fontSize: 20 }} />
                        </Link>

                        <button
                          onClick={() => onSendEmail(application.id)}
                          className="p-2 text-gray-400 hover:text-purple-600 rounded-full hover:bg-gray-100 transition"
                          title="Письмо"
                        >
                          <EmailIcon sx={{ fontSize: 20 }} />
                        </button>
                        <button
                          onClick={() => onDelete(application.id)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100 transition"
                          title="Удалить"
                        >
                          <DeleteIcon sx={{ fontSize: 20 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                    Заявки не найдены
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ApplicationsTable;